import {
  integrationsApi,
  useGetCurrentUserQuery,
  usePostEventMutation,
} from '@carboncopyco/data';
import {
  DownOutlineIcon,
  PlayIcon,
  ShareOutlineIcon,
  ShowOutlineIcon,
} from '@carboncopyco/icons';
import { EventAction, EventTarget, type Role } from '@carboncopyco/types';
import { Button, Separator } from '@carboncopyco/ui';
import { isDesktop, useDemoAnalytics } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DemoLinkModal } from '../../DemoLinkModal';
import { GiveADemoModal } from '../../PresentationModals';
import { useOverlayPageProps } from './OverlayPageContext';
import { OverlayPageDropdown } from './OverlayPageDropdown';

export const OverlayPageActionButtons = () => {
  const [postEvent] = usePostEventMutation();
  const [
    showGiveADemoModal,
    { setTrue: setShowGiveADemoModal, setFalse: setHideGiveADemoModal },
  ] = useBoolean();
  const {
    goToDemoPreview,
    handleCreatePresentation,
    demoId,
    organizationId,
    rootPath,
    playPresentation,
    desktopDomain,
    workspaceId,
    readOnly,
  } = useOverlayPageProps();

  const flags = useFlags();
  const [openDemoLinkModal, setOpenDemoLinkModal] = useState(false);
  const { trackDemoClickShareInternally, trackDemoClickPresent } =
    useDemoAnalytics('overlay_info_page_top_actions');
  const prefetchIntegrations = integrationsApi.usePrefetch('getIntegrations');
  useEffect(() => {
    if (flags?.integrations) {
      prefetchIntegrations({
        organizationId: Number(organizationId),
      });
    }
  }, []);

  const { data: userData } = useGetCurrentUserQuery();
  const userRole: Role = userData?.user?.roles?.[organizationId];
  const navigate = useNavigate();
  const { trackDemoClickPreview } = useDemoAnalytics(
    'overlay_info_page_top_actions'
  );
  return (
    <div className="flex items-center gap-3">
      {userRole !== 'presenter' &&
        (!readOnly ? (
          <>
            {' '}
            <OverlayPageDropdown
              triggerElement={
                <Button
                  link
                  blue
                  rightIcon={<DownOutlineIcon />}
                  label="Actions"
                />
              }
            />
            <div className="h-7 pr-2">
              <Separator />
            </div>
          </>
        ) : (
          <Button
            secondary
            leftIcon={<ShowOutlineIcon />}
            label="Preview"
            onClick={() => {
              trackDemoClickPreview();
              postEvent({
                organizationId,
                workspaceId,
                demoId: String(demoId),
                action: EventAction.PREVIEWED,
                target: EventTarget.DEMO,
              });
              goToDemoPreview();
            }}
          />
        ))}
      {!readOnly && flags.demoLink && (
        <Button
          secondary
          label="Share"
          leftIcon={<ShareOutlineIcon />}
          onClick={() => {
            trackDemoClickShareInternally();
            setOpenDemoLinkModal(true);
          }}
        />
      )}
      <Button
        onClick={() => {
          trackDemoClickPresent();
          if (isDesktop()) {
            handleCreatePresentation?.();
            return;
          }
          setShowGiveADemoModal();
        }}
        leftIcon={<PlayIcon />}
        label="Give a demo"
      />

      {openDemoLinkModal && (
        <DemoLinkModal
          workspaceId={workspaceId}
          assetId={demoId}
          assetType="demo"
          organizationId={organizationId}
          isOpen={openDemoLinkModal}
          onClose={() => setOpenDemoLinkModal(false)}
          role={userRole}
        />
      )}
      {showGiveADemoModal && (
        <GiveADemoModal
          workspaceId={workspaceId}
          onClose={setHideGiveADemoModal}
          open={showGiveADemoModal}
          demoId={demoId}
          organizationId={organizationId}
          goToDemoPreview={goToDemoPreview}
          goToPresentationPage={playPresentation}
          goToDemoPresentations={() => {
            navigate(`${rootPath}/presentations`);
          }}
          desktopDomain={desktopDomain}
        />
      )}
    </div>
  );
};
